import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";

const lines = ["We build websites", "people remember."];

export const Hero = () => {
  const ref = useRef(null);
  const glowRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const ringsRotate = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
  const tiltY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mx.set(x / rect.width - 0.5);
    my.set(y / rect.height - 0.5);
    if (glowRef.current) {
      glowRef.current.style.background = `radial-gradient(560px at ${x}px ${y}px, rgba(37,99,235,0.09), transparent 70%)`;
    }
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <div className="pointer-events-none absolute right-[8%] top-1/2 hidden h-[560px] w-[560px] -translate-y-1/2 rounded-full bg-[#2563EB] opacity-[0.06] blur-[130px] lg:block" />

      <motion.div
        style={{ rotate: ringsRotate }}
        className="pointer-events-none absolute right-[2%] top-1/2 hidden w-[640px] -translate-y-1/2 lg:block"
        aria-hidden="true"
        data-testid="hero-rings"
      >
        <svg width="640" height="640" viewBox="0 0 720 720" fill="none">
          <circle cx="360" cy="360" r="352" stroke="rgba(255,255,255,0.05)" />
          <circle cx="360" cy="360" r="300" stroke="rgba(255,255,255,0.06)" />
          <circle cx="360" cy="360" r="240" stroke="rgba(255,255,255,0.04)" />
          <circle cx="360" cy="360" r="176" stroke="rgba(255,255,255,0.05)" />
          <g className="hero-orbit">
            <circle cx="360" cy="360" r="270" stroke="rgba(37,99,235,0.55)" strokeDasharray="3 240" strokeLinecap="round" />
            <circle cx="360" cy="60" r="3.5" fill="#2563EB" />
          </g>
        </svg>
      </motion.div>

      <div
        className="pointer-events-none absolute right-[2%] top-1/2 hidden w-[640px] -translate-y-1/2 items-center justify-center lg:flex"
        style={{ perspective: 900 }}
      >
        <motion.img
          src="/kronos-mark-light.webp"
          alt=""
          aria-hidden="true"
          data-testid="hero-k-mark"
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 0.9, scale: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ rotateX: tiltX, rotateY: tiltY }}
          className="w-[240px] select-none"
        />
      </div>

      <div ref={glowRef} className="pointer-events-none absolute inset-0" />
      <motion.div
        className="pointer-events-none absolute -top-40 left-1/4 h-[480px] w-[480px] rounded-full bg-[#2563EB] opacity-[0.05] blur-[140px]"
        animate={{ x: [0, 80, 0], y: [0, 40, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="noise-overlay pointer-events-none absolute inset-0" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-36 sm:px-8 lg:px-12"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-[#5A5E6B]"
          data-testid="hero-eyebrow"
        >
          Kronos — Web Design & Development Studio
        </motion.p>

        <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-[#F4F4F6] sm:text-5xl lg:text-6xl">
          {lines.map((line, li) => (
            <span key={li} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3 + li * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {li === 1 ? (
                  <>
                    people <span className="text-[#2563EB]">remember.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#9498A4] sm:text-lg"
          data-testid="hero-subcopy"
        >
          Kronos designs and develops high-performance websites for businesses that care
          about how they look, how they communicate, and how they convert.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-start-project-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#1D4ED8]"
          >
            Start a Project
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/work"
            data-testid="hero-view-work-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-[#F4F4F6] transition-colors duration-300 hover:border-[#2563EB]/60 hover:bg-white/5"
          >
            View Our Work
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-16 flex flex-col gap-2"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5A5E6B]" data-testid="hero-microcopy">
            Strategy · Design · Development · Optimization
          </p>
          <p className="text-sm text-[#5A5E6B]">
            Built for businesses ready to look like the leader in their market.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5A5E6B]">Scroll</span>
        <motion.span
          className="h-10 w-px bg-gradient-to-b from-[#2563EB] to-transparent"
          animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};
